'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { ACTIVE_ORDERS_QK, TRADE_HISTORY_QK } from '@/lib/api/history/hooks';
import type { ActiveOrder, ActiveOrderPageResponse } from '@/lib/api/history/models';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { WS_ENDPOINTS } from '@/lib/config/websocket';
import { useWebSocket } from '@/lib/hooks/useWebSocket';
import type { OrderUpdateMessage } from '@/lib/types/websocket';

export type OrderUpdateHandler = (update: OrderUpdateMessage) => void;

export function useOrderHistoryWebSocket(
  onUpdate?: OrderUpdateHandler,
  options?: {
    autoConnect?: boolean;
  },
) {
  const { isAuthenticated } = useAuthContext();
  const queryClient = useQueryClient();
  const onUpdateRef = useRef<OrderUpdateHandler | undefined>(undefined);

  const { autoConnect: autoConnectOption = true } = options || {};

  // Keep latest handler in ref to avoid re-creating connect() on each render
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const handleOrderUpdate = useCallback(
    (msg: OrderUpdateMessage) => {
      const { d: data } = msg;

      if (!data) {
        console.warn('OrderUpdate: missing data in message', msg);
        return;
      }

      // Call custom handler if provided
      onUpdateRef.current?.(msg);

      const outcomeId = data.o; // eventOutcomeId

      // useActivityHistoryWebSocket pushes optimistic per-match rows for the
      // History tab. On FILLED/EXECUTED, invalidate trade history so REST
      // refetches authoritative data (with proper UUID/assetId) — this powers
      // the avg-price calc in EventOpenPositions* which the WS rows can't.
      if (data.st === 'FILLED' || data.st === 'EXECUTED') {
        queryClient.invalidateQueries({ queryKey: [TRADE_HISTORY_QK, outcomeId] });
        queryClient.setQueryData<boolean>([TRADE_HISTORY_QK, 'has-data', data.e, outcomeId], true);
      }

      // Update active orders cache directly from ORDER_UPDATE message
      queryClient.setQueryData<ActiveOrderPageResponse>(
        [ACTIVE_ORDERS_QK, outcomeId],
        (oldData) => {
          if (!oldData) {
            // If no cache exists, create new data structure with the order
            if (data.st === 'ACTIVE' || data.st === 'PENDING' || data.st === 'PARTIALLY_FILLED') {
              const newOrder: ActiveOrder = {
                id: data.i,
                userId: data.u,
                eventId: data.e,
                eventOutcomeId: data.o,
                assetId: data.a,
                originalQuantity: data.oq,
                side: data.si,
                type: data.t,
                price: data.p,
                matchedQuantity: data.mq ?? 0,
                quantity: data.q,
                status: data.st,
                expireAt: data.ex,
              };
              return {
                items: [newOrder],
                page: 0,
                size: 1,
                totalPages: 1,
              };
            }
            return oldData;
          }

          const existingOrderIndex = oldData.items.findIndex((order) => order.id === data.i);

          // If order status is CANCELLED, FILLED, REJECTED, or EXECUTED - remove from active orders
          if (
            data.st === 'CANCELLED' ||
            data.st === 'FILLED' ||
            data.st === 'REJECTED' ||
            data.st === 'EXECUTED'
          ) {
            if (existingOrderIndex !== -1) {
              const newData = {
                ...oldData,
                items: oldData.items.filter((order) => order.id !== data.i),
              };
              return newData;
            }
            return oldData;
          }

          // If order is ACTIVE, PENDING, or PARTIALLY_FILLED - add or update
          const updatedOrder: ActiveOrder = {
            id: data.i,
            userId: data.u,
            eventId: data.e,
            eventOutcomeId: data.o,
            assetId: data.a,
            originalQuantity: data.oq,
            side: data.si,
            type: data.t,
            price: data.p,
            matchedQuantity: data.mq ?? 0,
            quantity: data.q,
            status: data.st,
            expireAt: data.ex,
          };

          if (existingOrderIndex !== -1) {
            // Update existing order
            const newItems = [...oldData.items];
            newItems[existingOrderIndex] = updatedOrder;
            const newData = {
              ...oldData,
              items: newItems,
            };
            return newData;
          } else {
            // Add new order at the beginning (newest first)
            const newData = {
              ...oldData,
              items: [updatedOrder, ...oldData.items],
            };
            return newData;
          }
        },
      );

      // Update has-data cache for conditional tab rendering
      const hasData = queryClient.getQueryData<ActiveOrderPageResponse>([
        ACTIVE_ORDERS_QK,
        outcomeId,
      ]);
      queryClient.setQueryData<boolean>(
        [ACTIVE_ORDERS_QK, 'has-data', outcomeId],
        hasData ? hasData.items.length > 0 : false,
      );
    },
    [queryClient],
  );

  const { send, ...orderHistorySocket } = useWebSocket({
    url: WS_ENDPOINTS.orderHistory,
    autoConnect: autoConnectOption && isAuthenticated,
    reconnect: true,
    onOpen: () => {},
    onOrderUpdate: handleOrderUpdate,
    onErrorMessage: (errorMsg) => {
      console.error('Order History WebSocket error:', errorMsg);
    },
    onClose: (_event) => {},
  });

  // No need for additional useEffect - useWebSocket already manages connection via autoConnect parameter

  return {
    ...orderHistorySocket,
    send,
  };
}
